import {
    storeSearchRedirectTerm,
    trackNumericProduct,
    trackProductSelection,
    trackSearchResultsFallback,
    trackSuggestionSelection,
} from '../analytics';
import type {
    AssistedResultItem,
    AssistedSearchState,
    PrimaryResultItem,
    ProductSearchItem,
    SearchItem,
    SearchRedirect,
} from '../search.types';
import type { RecentSearches } from './recent-searches';
import {
    completeCoreRedirectSubmit,
    getProductResultUrl,
    getSearchResultUrl,
    navigateToSearchResult,
    startApplicationSearchSubmit,
} from './search-navigation';

type SearchQueryState = {
    assistedSearch: AssistedSearchState;
    captureSearchSelection: (item: SearchItem) => void;
    completeSearchSelection: (item: SearchItem) => void;
    getCurrentQuery: () => string;
    query: string;
};

type SearchResults = {
    getApplicationRedirects: (query: string) => readonly SearchRedirect[];
    getCurrentCoreQuery: () => string;
    getCurrentRedirects: () => readonly SearchRedirect[];
    getLoadedProductShortcut: (query: string) => ProductSearchItem | undefined;
    refresh: (query: string) => void;
    resetSnapshot: () => void;
};

type CreateSearchCommandsOptions = {
    isActive: () => boolean;
    queryState: SearchQueryState;
    recentSearches: RecentSearches | null;
    results: SearchResults;
    trackApplicationSubmit: (options: { assistedSearch: AssistedSearchState; query: string }) => void;
};

export function createSearchCommands({
    isActive,
    queryState,
    recentSearches,
    results,
    trackApplicationSubmit,
}: CreateSearchCommandsOptions) {
    const addQuerySelectionToRecentSearches = (item: SearchItem) => {
        if (item.kind === 'query') {
            recentSearches?.add({ label: item.label, category: item.category });
        }
    };

    const selectSearchResult = async (result: PrimaryResultItem) => {
        const { item } = result;
        queryState.captureSearchSelection(item);

        results.refresh(item.kind === 'product' ? queryState.query : item.label);
        if (item.kind === 'product' && !item.hasProductId) return;

        const navigationQuery = queryState.getCurrentQuery();
        const url =
            item.kind === 'product'
                ? getProductResultUrl(item, navigationQuery)
                : getSearchResultUrl(item, navigationQuery);

        if (item.kind === 'product') {
            trackProductSelection({ item, query: navigationQuery, url });
            navigateToSearchResult(url);
            return;
        }

        trackSuggestionSelection({ isPrimarySelection: true, item, query: navigationQuery, url });
        queryState.completeSearchSelection(item);
        addQuerySelectionToRecentSearches(item);
        navigateToSearchResult(url);
    };

    const completeAssistedSearch = async (result: AssistedResultItem) => {
        const { item } = result;
        queryState.captureSearchSelection(item);

        results.refresh(item.label);
        const selectionQuery = queryState.getCurrentQuery();
        trackSuggestionSelection({
            isPrimarySelection: false,
            item,
            query: selectionQuery,
            url: getSearchResultUrl(item, selectionQuery),
        });
        queryState.completeSearchSelection(item);
        addQuerySelectionToRecentSearches(item);
    };

    const submitSearch = async () => {
        const currentQuery = queryState.getCurrentQuery();
        if (!currentQuery.trim()) return undefined;

        const applicationSubmit = startApplicationSearchSubmit({
            applicationRedirects: results.getApplicationRedirects(currentQuery),
            assistedSearch: queryState.assistedSearch,
            isActive,
            loadedProductShortcut: results.getLoadedProductShortcut(currentQuery),
            onFetchedProduct: () => trackNumericProduct(currentQuery),
            onLoadedProduct: () => trackNumericProduct(currentQuery),
            onRedirect: () => storeSearchRedirectTerm(currentQuery),
            onSearchResultsFallback: url => trackSearchResultsFallback(currentQuery, url),
            onSubmit: () =>
                trackApplicationSubmit({
                    assistedSearch: queryState.assistedSearch,
                    query: currentQuery,
                }),
            query: currentQuery,
        });

        const currentCoreQuery = results.getCurrentCoreQuery();
        if (currentCoreQuery) {
            recentSearches?.add({ label: currentCoreQuery });
        }
        completeCoreRedirectSubmit(results.getCurrentRedirects());
        const destination = await applicationSubmit;

        if (destination === 'search-results') results.resetSnapshot();
        return destination;
    };

    return {
        completeAssistedSearch,
        selectSearchResult,
        submitSearch,
    };
}
